"use client";

import { PropsWithChildren, ReactNode, useEffect } from "react";
import { ErrorBoundary } from "@sentry/nextjs";
import { getSortTermLocalStorage, setSortTermLocalStorage } from "utils/expirableLocalStorage";
import { isRecoverableChunkError } from "utils/recoverableChunkError";
const UNKNOWN_ERROR_MESSAGE = "unknown error";
type ErrorWithDigest = Error & {
  digest?: string;
};

// Generate a fast, unique 32-bit integer hash and convert to a short alphanumeric string
const generateErrorHash = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36); // Use absolute value for clean keys
};
const safeReadErrorProp = <K extends keyof ErrorWithDigest,>(error: ErrorWithDigest, key: K): ErrorWithDigest[K] | undefined => {
  try {
    return error[key];
  } catch {
    return undefined;
  }
};
export const getErrorDetails = (error?: ErrorWithDigest) => {
  if (!error) return "NA";
  const details = {
    message: safeReadErrorProp(error, "message") || UNKNOWN_ERROR_MESSAGE,
    digest: safeReadErrorProp(error, "digest"),
    name: safeReadErrorProp(error, "name"),
    stack: safeReadErrorProp(error, "stack")
  };
  try {
    return JSON.stringify(details);
  } catch {
    return details.message;
  }
};
const ErrorRegisterAndToast = ({
  error,
  eventId,
  reportToMixpanel
}: {
  error: Error;
  eventId: string;
  reportToMixpanel: boolean;
}) => {
  useEffect(() => {
    if (!error) {
      return;
    }

    // The Sentry ErrorBoundary has already captured this error (with the React
    // component stack), so only log that event to Mixpanel here.
    const sendToMixpanel = () => {
      if (!reportToMixpanel) return;
      void (async () => {
        const {
          mixpanelV2Client
        } = await import("services/mixpanelv2");
        const path = typeof window !== "undefined" ? window.location.href : "NA";
        mixpanelV2Client.WEBSITE_ERROR({
          pageType: "errorToast",
          path,
          errorDetail: getErrorDetails(error),
          sentry_error_id: eventId || undefined
        });
      })();
    };
    try {
      if (!isRecoverableChunkError(error)) {
        sendToMixpanel();
        return;
      }
      const errorHash = generateErrorHash(error.message || UNKNOWN_ERROR_MESSAGE);

      // Cross-component 2-second deduplication lock
      const dedupeKey = `dedupe_err_${errorHash}`;
      const isDuplicate = (window[dedupeKey] as boolean);
      if (isDuplicate) {
        // Sibling boundaries hit by the same stale chunk; handled by the first one.
        return;
      }
      window[dedupeKey] = true; // Lock it for 2 seconds immediately
      setTimeout(() => {
        delete window[dedupeKey];
      }, 2000);

      // Namespace the retry trackers with the hash so distinct chunk errors
      // don't interfere with each other's retry logic.
      const fastFailKey = `isChunkLoadError_${errorHash}`;
      const retryCountKey = `chunkLoadRetryCount_${errorHash}`;

      // Reload finished quickly but the error persists: log it and stop.
      if (getSortTermLocalStorage(fastFailKey)) {
        sendToMixpanel();
        return;
      }

      // 5-minute circuit breaker for slow reloads
      const rawCount = getSortTermLocalStorage(retryCountKey);
      const retryCount = rawCount ? parseInt(rawCount, 10) : 0;
      const MAX_RETRIES = 1;
      if (retryCount >= MAX_RETRIES) {
        sendToMixpanel();
        return;
      }
      setSortTermLocalStorage(fastFailKey, "true", 10); // Set 10s fast-fail flag
      setSortTermLocalStorage(retryCountKey, (retryCount + 1).toString(), 300); // Set 5m counter

      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (registerError) {
      console.error("Failed to record boundary error", registerError);
    }
  }, [error, eventId, reportToMixpanel]);
  return null; // no toast
};
type FallbackRender = (props: {
  error: Error;
  eventId: string;
}) => ReactNode;
const ErrorBoundaryWithToast = ({
  location = "errorToast",
  fallback,
  reportToMixpanel = true,
  children
}: PropsWithChildren<{
  location?: string;
  fallback?: ReactNode | FallbackRender;
  // Turn off when the fallback logs its own Mixpanel event (e.g. the 500 page).
  reportToMixpanel?: boolean;
}>) => {
  return <ErrorBoundary beforeCapture={(scope, _error, componentStack) => {
    scope.setTag("ws_location", location);
    scope.setExtra("componentStack", componentStack);
    if (typeof window !== "undefined") {
      scope.setExtra("url", window.location.href);
    }
  }} fallback={({
    error,
    eventId
  }) => <>
          <ErrorRegisterAndToast error={(error as Error)} eventId={eventId} reportToMixpanel={reportToMixpanel} />
          {typeof fallback === "function" ? fallback({
      error: (error as Error),
      eventId
    }) : fallback}
        </>} data-sentry-element="ErrorBoundary" data-sentry-component="ErrorBoundaryWithToast" data-sentry-source-file="ErrorBoundaryWithToast.tsx">
      {children}
    </ErrorBoundary>;
};
export default ErrorBoundaryWithToast;